import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, MenuItem, Switch } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useNavigate } from "react-router-dom";
import { useGetFieldAdminHSAttendanceQuery, useSaveFieldAdminHSAttendanceMutation } from "../fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { installerLabel, FONT_FAMILY } from "../designTokens";
import { HSPageShell, HSSectionLabel, HSFooterActions } from "./HSStepShell";
import { FAErrorCard, FASkeletonRows } from "../FAStates";
import { InstallerAvatar, mergeRosterRows, useTeamRoster } from "./InstallerRoster";
import { FASelect } from "../StockFormShell";
import { FACheckbox, useConfirm } from "../FAControls";
import { FAEmptyState } from "../FALayout";

const SEVERITIES = ["Low", "Med", "High"];

export default function Attendance() {
  const navigate = useNavigate();
  const { data, isSuccess, isError, refetch } = useGetFieldAdminHSAttendanceQuery();
  const [saveAttendance, { isLoading: isSaving }] = useSaveFieldAdminHSAttendanceMutation();

  const { installers, nameById, isRosterReady, isRosterError, refetchRoster } = useTeamRoster();
  const [records, setRecords] = useState(null);
  const [openNoteFor, setOpenNoteFor] = useState(null);
  const { ask, confirmDialog } = useConfirm();

  // The register is the whole team — an installer with no record yet is exactly the
  // "not verified" case this page exists to resolve, so they need a row to tick.
  useEffect(() => {
    if (!isSuccess || !isRosterReady || records !== null) return;
    setRecords(mergeRosterRows(installers, data, (record, installerId) => ({
      installer_id: installerId,
      fa_verified: record?.fa_verified ?? false,
      conduct_flag: record?.conduct_flag ?? false,
      severity: record?.severity || "Med",
      note: record?.note || "",
    })));
  }, [isSuccess, data, records, installers, isRosterReady]);

  if (records === null) {
    return (
      <HSPageShell step={{ stepNumber: 3, title: "Attendance", countLabel: "" }}>
        {/* Blocked on the roster as well as this step's own data — see Tools. */}
        {isError || isRosterError
          ? <FAErrorCard onRetry={isRosterError ? refetchRoster : refetch} />
          : <FASkeletonRows count={5} />}
      </HSPageShell>
    );
  }

  const verifiedCount = records.filter((r) => r.fa_verified).length;

  const patchRecord = (installerId, patch) => {
    setRecords((prev) => prev.map((r) => (r.installer_id !== installerId ? r : { ...r, ...patch })));
  };

  // Returns whether every row landed. HSFooterActions treats a false return as "the
  // save failed, stay put" — a handler that returns nothing navigates away regardless,
  // which is how a failed save used to lose every tick with no error shown.
  const saveAll = async () => {
    const results = await Promise.all(records.map((r) => saveAttendance(r)));
    return results.every((result) => !result.error);
  };

  const handleMarkComplete = async () => {
    if (records.length === 0 || verifiedCount < records.length) {
      const proceed = await ask({
        title: "Complete with installers unverified?",
        body: records.length === 0
          ? "No installers on this team\u2019s roster have been verified. Mark attendance complete anyway?"
          : `${records.length - verifiedCount} of ${records.length} installers are not yet verified. Mark attendance complete anyway?`,
        confirmLabel: "Mark complete",
        cancelLabel: "Keep checking",
        danger: true,
      });
      if (!proceed) return;
    }
    if (!(await saveAll())) return;
    navigate("/field-admin/hs/toolbox/");
  };

  return (
    <HSPageShell step={{ stepNumber: 3, title: "Attendance", countLabel: `${verifiedCount} / ${records.length} verified` }}>

      {/* Design shows clock in/out time pickers, but AttendanceVerification has no time
          fields at all — it's a verification flag layer, not a time-tracking store. The
          real clock-in/out times live in IMS's Attendance tab (TODO(ims-connection) on
          the backend view). Verifying here just confirms the FA has sighted the installer. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", backgroundColor: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: "12px", px: "14px", py: "12px", mb: "18px" }}>
        <Box sx={{ width: "30px", height: "30px", borderRadius: "8px", backgroundColor: "rgba(34,197,94,0.15)", color: "#4ADE80", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <SendOutlinedIcon sx={{ fontSize: "1rem" }} />
        </Box>
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
          FA verification here rolls up to PM oversight. The canonical per-technician daily clock lives in IMS ·
          installer-company → Attendance — this page records that you sighted each installer, not their hours.
        </Typography>
      </Box>

      <HSSectionLabel right={`${records.length} installer${records.length === 1 ? "" : "s"}`}>Installers</HSSectionLabel>

      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
        {records.length === 0 ? (
          <FAEmptyState py="30px">No installers on this team's roster.</FAEmptyState>
        ) : (
          records.map((r, index) => (
            <Box key={r.installer_id} sx={{ borderBottom: index === records.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)", px: "18px", py: "14px", backgroundColor: r.fa_verified ? "transparent" : "rgba(253,185,64,0.04)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <InstallerAvatar name={nameById.get(r.installer_id)} id={r.installer_id} />
                <Box sx={{ flexGrow: 1, minWidth: "100px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.92rem" }}>
                    {installerLabel(nameById.get(r.installer_id), r.installer_id)}
                  </Typography>
                  {!r.fa_verified ? (
                    <Box component="span" sx={{ fontSize: "0.64rem", fontWeight: 700, px: "7px", py: "2px", borderRadius: "4px", textTransform: "uppercase", backgroundColor: "rgba(253,185,64,0.14)", color: AMBER }}>
                      Not verified
                    </Box>
                  ) : null}
                </Box>

                <FACheckbox
                  compact
                  checked={r.fa_verified}
                  onChange={() => patchRecord(r.installer_id, { fa_verified: !r.fa_verified })}
                  label="Verified"
                  labelSx={{ fontSize: "0.8rem", color: r.fa_verified ? "#4ADE80" : "rgba(255,255,255,0.6)" }}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em", mb: "2px" }}>
                      Conduct flag
                    </Typography>
                    <Switch
                      size="small"
                      checked={r.conduct_flag}
                      onChange={() => patchRecord(r.installer_id, { conduct_flag: !r.conduct_flag })}
                      sx={{
                        m: 0,
                        "& .MuiSwitch-switchBase.Mui-checked": { color: AMBER },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: AMBER, opacity: 0.5 },
                      }}
                    />
                  </Box>
                  {/* The design puts severity inline beside the toggle, not on its own row. */}
                  {r.conduct_flag ? (
                    // .severity-select — a compact trigger sized to its own text, not the
                    // full-width bordered field FASelect renders. Values from the design's
                    // own override of .custom-select__trigger.
                    <FASelect
                      size="small"
                      fullWidth={false}
                      value={r.severity}
                      onChange={(e) => patchRecord(r.installer_id, { severity: e.target.value })}
                      sx={{
                        width: "auto",
                        // Doubled class: MUI sets its own padding-right on .MuiSelect-select to
                        // clear the icon, and wins on specificity against a single class.
                        "& .MuiSelect-select.MuiSelect-select": {
                          paddingRight: "22px",
                          backgroundColor: "#0D1117",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: "6px",
                          paddingTop: "4px",
                          paddingBottom: "4px",
                          paddingLeft: "8px",
                          color: "#fff",
                          fontFamily: FONT_FAMILY,
                          fontSize: "0.74rem",
                          minHeight: "unset",
                          lineHeight: 1.4,
                        },
                        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                        // .severity-select .custom-select__chevron is 10px.
                        "& .MuiSelect-icon": { width: "10px", height: "10px", top: "calc(50% - 5px)", right: "6px", color: "rgba(255,255,255,0.5)" },
                      }}
                    >
                      {SEVERITIES.map((sev) => (
                        <MenuItem key={sev} value={sev}>{sev}</MenuItem>
                      ))}
                    </FASelect>
                  ) : null}
                </Box>

                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpenNoteFor(openNoteFor === r.installer_id ? null : r.installer_id)}
                  sx={{
                    fontSize: "0.72rem", fontWeight: 600, px: "10px", py: "6px", borderRadius: "6px", cursor: "pointer",
                    fontFamily: FONT_FAMILY, whiteSpace: "nowrap",
                    border: `1px solid ${r.note ? "rgba(253,185,64,0.3)" : "rgba(255,255,255,0.1)"}`,
                    backgroundColor: r.note ? "rgba(253,185,64,0.05)" : "transparent",
                    color: r.note ? AMBER : "rgba(255,255,255,0.5)",
                  }}
                >
                  {openNoteFor === r.installer_id ? "− Hide" : "+ Note"}
                </Box>
              </Box>

              {openNoteFor === r.installer_id ? (
                <Box sx={{ mt: "10px" }}>
                  <textarea
                    value={r.note}
                    onChange={(e) => patchRecord(r.installer_id, { note: e.target.value })}
                    placeholder="Conduct / attendance note (visible to PM)"
                    rows={2}
                    style={{ width: "100%", boxSizing: "border-box", resize: "vertical", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#0d1117", color: "#fff", padding: "8px 10px", fontSize: "0.8rem" }}
                  />
                </Box>
              ) : null}
            </Box>
          ))
        )}
      </Box>

      <HSFooterActions onSaveExit={saveAll} onMarkComplete={handleMarkComplete} markCompleteDisabled={isSaving} />
      {confirmDialog}
    </HSPageShell>
  );
}
